'use client';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Container,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React from 'react';
import slugify from 'react-slugify';

import LinkCustom from '@/app/_components/link/LinkCustom';
import { ReactComponent as HomeIcon } from '@/app/_icons/home.svg';
import { ReactComponent as VectorLocation } from '@/app/_icons/location-vector.svg';
import { useRoutesConfig } from '@/hooks/routes-config';
import { VehicleExtended } from '@/types/details-car/details-car.types';

import { PriceFormat } from '../price-format/price-format';

import Styles from './HeaderSection.module.scss';

const HeaderSection = ({ vehicleInfo }: { vehicleInfo: VehicleExtended }) => {
  const router = useRouter();
  const routes = useRoutesConfig();

  const { nowPrice, price } = vehicleInfo;
  const hasNowPrice = nowPrice && +nowPrice > 0 && +nowPrice < +price;

  const urls = [
    {
      url: `${routes.usedCars.path}`,
      name: 'Certified Pre-Owned',
    },
    {
      url: `${routes.usedCars.path}/${slugify(vehicleInfo?.brand)}`,
      name: vehicleInfo.brand,
    },
    {
      url: `${routes.usedCars.path}/${slugify(vehicleInfo?.brand)}/${slugify(vehicleInfo?.range_id)}`,
      name: vehicleInfo.range_id,
    },
    {
      url: `${routes.usedCars.path}/${slugify(vehicleInfo?.brand)}/${slugify(vehicleInfo?.range_id)}/${slugify(vehicleInfo?.id)}`,
      name: vehicleInfo?.title,
    },
  ];

  return (
    <Container
      maxW={'1328'}
      width={'100%'}
      className={Styles.headerSectionWrapper}
      paddingTop={{ base: 12, md: 14 }}
      paddingX={{ base: 4, xl: 0 }}
    >
      <Breadcrumb marginBottom={12} className={Styles.breadcrumb}>
        {urls.map((path, i) => (
          <BreadcrumbItem key={i} isCurrentPage={i === urls.length - 1}>
            <BreadcrumbLink href={path.url} className={Styles.breadcrumbLink}>
              {!i && <HomeIcon />} <Text as={'span'}>{path.name}</Text>
            </BreadcrumbLink>
          </BreadcrumbItem>
        ))}
      </Breadcrumb>
      <Box className={Styles.bakcBtnContainer} marginBottom={10}>
        <Button
          onClick={() => router.back()}
          className={Styles.backBtn}
          padding={0}
          role="button"
        >
          <ChevronLeftIcon role="button" />
          <Box className={Styles.backBtnText} role="button">
            Back to Results
          </Box>
        </Button>
      </Box>

      <Flex
        justifyContent={{ base: 'center', lg: 'space-between' }}
        direction={{ base: 'column', lg: 'row' }}
        alignItems={{ base: 'center', lg: 'flex-start' }}
        gap={6}
        textAlign={{ base: 'center', lg: 'left' }}
      >
        <Box className={Styles.headingElement}>
          <Heading as="h1" className={Styles.titleLg}>
            {vehicleInfo?.title}
          </Heading>
        </Box>
        <Box
          className={Styles.headingElement}
          display={'flex'}
          flexDirection={'column'}
          alignItems={{ base: 'center', lg: 'flex-end' }}
        >
          <Heading as="h2" className={Styles.titleMd} marginBottom={2}>
            <PriceFormat
              value={
                +vehicleInfo.nowPrice > +vehicleInfo.price
                  ? vehicleInfo.nowPrice
                  : hasNowPrice
                    ? vehicleInfo.nowPrice
                    : vehicleInfo.price
              }
              thousandSeparator=" "
              prefix="R"
            />
          </Heading>
          {vehicleInfo?.monthlyRepayment ? (
            <Text className={Styles.titleText}>
              From{' '}
              <PriceFormat
                value={vehicleInfo.monthlyRepayment}
                thousandSeparator=","
                prefix="R"
                suffix=" p/m"
              />
            </Text>
          ) : null}
        </Box>
      </Flex>
      <Flex
        justifyContent={{ base: 'center', lg: 'space-between' }}
        direction={{ base: 'column', lg: 'row' }}
        alignItems={{ base: 'center', lg: 'flex-start' }}
        gap={6}
        textAlign={{ base: 'center', lg: 'left' }}
        marginTop={{ base: '22px', sm: '0px' }}
      >
        <Link
          href={`/dealerships/${slugify(vehicleInfo?.dealership_id?.name)}`}
          passHref
        >
          <div className={Styles.dealershipLocation}>
            <VectorLocation /> {vehicleInfo?.dealership_id?.name}
          </div>
        </Link>
        {vehicleInfo?.digitalTwinUrl &&
          vehicleInfo?.digitalTwinUrl?.length > 0 && (
            <Box mt={10}>
              <LinkCustom
                href={vehicleInfo?.digitalTwinUrl}
                extraClass={Styles.ctaBtn}
                target="_blank"
                rel="noopener noreferrer"
              >
                Vehicle History
              </LinkCustom>
            </Box>
          )}
      </Flex>
    </Container>
  );
};

export default HeaderSection;
